import { cloneDeep } from "lodash";
import { SimulatorOutput } from "../inputs";
import { IMonthlyPatientSplit } from "../patient-service";
import { GROUP_LABEL } from "./model.service";
import { modelPriorityList } from "../../details/utils/model-priority";

export function mergeIndications(output: SimulatorOutput): SimulatorOutput {
	const mergedMap = new Map<string, IMonthlyPatientSplit>();
	const getIndicationKey = (indicationId: string, sectionId?: string): string => {
		const groupMatch = sectionId?.match(new RegExp(`${GROUP_LABEL}(\\d+)`));
		const groupPart = groupMatch ? `${GROUP_LABEL}${groupMatch[1]}` : "";

		return `${indicationId}${groupPart}`;
	};

	for (const entry of output) {
		for (const monthly of entry.data) {
			for (const incomingPatient of monthly.activePatients) {
				const key = getIndicationKey(incomingPatient.indicationId, incomingPatient.sectionId);
				const dateKey = new Date(monthly.currentMonth).toISOString();
				const mapKey = `${key}_${dateKey}`;

				if (!mergedMap.has(mapKey)) {
					mergedMap.set(mapKey, {
						currentMonth: monthly.currentMonth,
						activePatients: [cloneDeep(incomingPatient)],
					});
				} else {
					const existingMonthly = mergedMap.get(mapKey)!;
					const matchingPatient = existingMonthly.activePatients.find((patient) => {
						const matchingGroupId = patient.sectionId?.split(GROUP_LABEL)[1];
						const incomingGroupId = incomingPatient.sectionId?.split(GROUP_LABEL)[1];
						const groupsAreIncompatible = matchingGroupId && incomingGroupId && matchingGroupId !== incomingGroupId;
						if (groupsAreIncompatible) {
							return false;
						}

						return (
							patient.treatmentStartMonth === incomingPatient.treatmentStartMonth &&
							patient.indicationId === incomingPatient.indicationId
						);
					});
					if (!matchingPatient) {
						existingMonthly.activePatients.push(cloneDeep(incomingPatient));
					} else {
						incomingPatient.distributedPacks.forEach((incomingPack) => {
							const targetPack = matchingPatient.distributedPacks.find(
								(tPack) =>
									tPack.packName === incomingPack.packName &&
									tPack.administrationDate.getTime() === incomingPack.administrationDate.getTime()
							);

							if (!targetPack) {
								return;
							}

							const priorityDiscounts = [...(targetPack.discounts || []), ...(incomingPack.discounts || [])].sort(
								(firstModel, secondModel) =>
									modelPriorityList.findIndex((model) => model === firstModel?.modelType) -
									modelPriorityList.findIndex((model) => model === secondModel?.modelType)
							);

							targetPack.discounts = priorityDiscounts;
						});
					}
				}
			}
		}
	}

	// Convert map back to the SimulatorOutput format
	const grouped = groupByIndication(mergedMap);

	return Object.entries(grouped).map(([indicationId, data]) => ({
		indication: [indicationId],
		data,
	}));
}

export function mergeIndicationForWeightGroups(output: SimulatorOutput): SimulatorOutput {
	const mergedMap = new Map<string, IMonthlyPatientSplit>();
	const getIndicationKey = (indicationId: string): string => {
		return `${indicationId}`;
	};

	for (const entry of output) {
		for (const monthly of entry.data) {
			for (const incomingPatient of monthly.activePatients) {
				const incomingPatientClone = cloneDeep(incomingPatient);
				const key = getIndicationKey(incomingPatient.indicationId);
				const dateKey = new Date(monthly.currentMonth).toISOString();
				const mapKey = `${key}_${dateKey}`;
				if (!mergedMap.has(mapKey)) {
					mergedMap.set(mapKey, {
						currentMonth: monthly.currentMonth,
						activePatients: [incomingPatientClone],
					});
				} else {
					const existingMonthly = mergedMap.get(mapKey)!;
					const matchingPatient = existingMonthly.activePatients.find((patient) => {
						return patient.treatmentStartMonth === incomingPatient.treatmentStartMonth;
					});

					if (!matchingPatient) {
						existingMonthly.activePatients.push(incomingPatientClone);
					} else {
						matchingPatient.numberOfPatients += incomingPatient.numberOfPatients ?? 0;
						matchingPatient.enrolledPatients += incomingPatient.enrolledPatients ?? 0;
						matchingPatient.numberOfActivePatients += incomingPatient.numberOfActivePatients ?? 0;
						matchingPatient.numberOfDiscontinuedPatients += incomingPatient.numberOfDiscontinuedPatients ?? 0;

						incomingPatientClone.distributedPacks.forEach((incomingPack) => {
							const targetPack = matchingPatient.distributedPacks.find(
								(tPack) =>
									tPack.packName === incomingPack.packName &&
									tPack.administrationDate.getTime() === incomingPack.administrationDate.getTime()
							);

							if (!targetPack) {
								return;
							}

							targetPack.quantity += incomingPack.quantity;
							if (targetPack.group) {
								targetPack.group = targetPack.group.map((group) => ({
									...group,
									packsPerPatient: targetPack.packsPerPatient,
								}));
							}

							if (targetPack.group && incomingPack.group) {
								targetPack.group.push(
									...incomingPack.group.map((groupItem) => ({
										...groupItem,
										packsPerPatient: groupItem.packsPerPatient ?? incomingPack.packsPerPatient,
									}))
								);
							}
							const priorityDiscounts = [...(targetPack.discounts || []), ...(incomingPack.discounts || [])].sort(
								(firstModel, secondModel) =>
									modelPriorityList.findIndex((model) => model === firstModel?.modelType) -
									modelPriorityList.findIndex((model) => model === secondModel?.modelType)
							);

							targetPack.discounts = priorityDiscounts;
						});
					}
				}
			}
		}
	}

	// Convert map back to the SimulatorOutput format
	const grouped = groupByIndication(mergedMap);

	return Object.entries(grouped).map(([indicationId, data]) => ({
		indication: [indicationId],
		data,
	}));
}

function groupByIndication<T>(mergedMap: Map<string, T>): Record<string, T[]> {
	const grouped: Record<string, T[]> = {};

	for (const [key, value] of mergedMap) {
		const indicationKey = key.split("_")[0];

		if (!grouped[indicationKey]) {
			grouped[indicationKey] = [];
		}
		grouped[indicationKey].push(value);
	}

	return grouped;
}
